import Button from "../core/Button";
import Strings from "../core/data/Strings";
import ResizableScene from "../core/ResizableScene";
import ScaleManager from "../core/ScaleManager";
import PlayerData from "../PlayerData";
import { CertificatePopup } from "../ui/CertificatePopup";
import { LevelSelectButton } from "../ui/LevelSelectButton";
import ARIA from "../utils/ARIA";
import Fonts from "../utils/Fonts";
import GameColors from "../utils/GameColors";

export type LevelData = {
	name: string;
	x: number;
	y: number;
	chunks: string[];
	length: number;
	progressBarMarkers: number;
	scoreRequirement: number;
	hasTutorial?: boolean;
};

export default class LevelSelectScene extends ResizableScene {
	private _onResize: () => void;
	private soundButton: Button;
	private isSoundOn: boolean;
	create(): void {
		super.create();
		this.isSoundOn = !this.soundManager.muted;
		const levels = this.cache.json.get("levels") as LevelData[];

		const saveData = PlayerData.Instance.saveData;
		//always unlock first level
		saveData.unlockedLevels["GreenCircle"] = true;

		const background = this.add.image(0, 0, "lvlSelectBG");

		const corinne = this.add.image(0, 0, "lvlCorinne");

		const headerGroup = this.add.container(0, 0);
		const headerBG = this.add.image(0, 0, "header");
		headerGroup.setSize(headerBG.width, headerBG.height);
		const headerText = this.add.text(0, 0, Strings.get("LevelSelect"), {
			fontSize: "70px",
			color: GameColors.WHITE,
			fontFamily: Fonts.MontserratExtraBold,
		});

		headerGroup.add([headerBG, headerText]);
		this.alignIn.Center(headerText, headerBG);

		const scoreGroup = this.add.container(0, 0);
		const scoreBG = this.add.image(0, 0, "scoreBG");
		scoreGroup.setSize(scoreBG.width, scoreBG.height);

		const totalScore = this.add.text(0, 0, PlayerData.Instance.saveData.totalScore.toFixed(0), {
			fontSize: "58px",
			color: GameColors.WHITE,
			fontFamily: Fonts.MontserratBold,
			align: "left",
		});
		totalScore.setOrigin(0, 0.5);
		this.alignIn.LeftCenter(totalScore, scoreBG, -160, 0);
		scoreGroup.add([scoreBG, totalScore]);

		const backButton = this.add.button(0, 0, "backBtn", null, this.back, this, true);

		const buttons: LevelSelectButton[] = [];
		let allLevelsPlayed = true;
		for (let i = 0; i < levels.length; i++) {
			const level = levels[i];
			const unlockLevelStatus = saveData.unlockedLevels[level.name.toString()];
			if (
				unlockLevelStatus == null ||
				unlockLevelStatus == undefined ||
				PlayerData.Instance.saveData.totalScore < levels[i].scoreRequirement
			) {
				saveData.unlockedLevels[level.name] = false;
			}
			if (!saveData.highScores[level.name]) allLevelsPlayed = false;
			const button = new LevelSelectButton(this, level, () => this.play(level), i + 1, true);
			this.add.existing(button);
			buttons.push(button);
		}
		PlayerData.Instance.save();
		//console.log(saveData);

		const soundButton = (this.soundButton = this.add.button(
			0,
			0,
			this.isSoundOn ? "soundBtnOn" : "soundBtnOff",
			null,
			this.onSoundPressed,
			this,
			true
		));
		soundButton.setScale(0.65);

		const certificateButton = this.add.button(
			0,
			0,
			"certificateButton",
			undefined,
			() => {
				const pop = new CertificatePopup(this);
				this.add.existing(pop);
			},
			this,
			true
		);
		if (!allLevelsPlayed) certificateButton.setVisible(false);

		this._onResize = (): void => {
			ScaleManager.ScaleBackgroundFromZone(background, this.viewport);
			this.alignIn.Center(background, this.viewport);

			const ratio = this.viewport.width / this.viewport.height - 5 / 3 < 0.01;
			this.alignIn.BottomLeft(corinne, this.viewport, 130, 200);
			this.alignIn.TopCenter(headerGroup, this.viewport, 0, 0);
			this.alignIn.TopLeft(backButton, this.viewport, -40, -40);
			this.alignIn.TopLeft(soundButton, this.viewport, -150, -5);
			this.alignIn.TopRight(scoreGroup, this.viewport, -40, ratio ? -140 : -40);
			this.alignIn.BottomRight(certificateButton, this.viewport, -40, -40);
			const buttonScale = ratio ? 0.7 : 1;
			for (let i = 0; i < buttons.length; i++) {
				const level = levels[i];
				const button = buttons[i];
				button.setScale(buttonScale);
				const topCenter = this.viewport.getCenter();
				topCenter.y -= 1080 / 2;
				button.setPosition(topCenter.x + level.x * buttonScale, topCenter.y + level.y);
			}
		};
		this.resize();

		backButton.setARIA("Back");
		soundButton.setARIA("Toggle Sound");
		headerText.setARIA(undefined, true);
		ARIA.addTextWithObject(scoreBG, `Total Score: ${PlayerData.Instance.saveData.totalScore.toFixed()}`);
		buttons.forEach((b) => b.setARIA());
		if (certificateButton.visible) certificateButton.setARIA("Certificate");
	}

	protected onSoundPressed(): void {
		//TODO: Mute sounds
		this.soundManager.muted = !this.soundManager.muted;
		this.sound.mute = this.soundManager.muted;
		this.music.muted = !this.music.muted;
		this.isSoundOn = !this.soundManager.muted;
		if (!this.isSoundOn) this.soundButton.setTexture("soundBtnOff");
		else this.soundButton.setTexture("soundBtnOn");
	}

	shutdown(): void {
		this._onResize = null;
		super.shutdown();
	}

	resize(): void {
		super.resize();
		if (this._onResize != null) this._onResize();
	}

	back(): void {
		this.scene.start("SplashScene");
	}

	play(level: LevelData): void {
		this.scene.start("PreloaderScene", { destinationScene: "GameScene", sceneState: { level: level } });
	}
}
